import { Duration } from 'dayjs/plugin/duration';

import { SBaseDisplay, SDuration } from '@summit/core';
import {
  DayOfWeek,
  DayOfWeekAbbrMapping,
  DaysOfWeek,
  DaysOfWeekToString,
  Holidays,
  HolidaysToString,
  Interval,
  MoneyFormatter,
  MonthEnum,
  NumberFormatter,
  NumberSortString,
  PercentageFormatter,
  RateFormatter,
  SummitFormattedText
} from '@summit/models';

import { EboObjectTimePeriod } from '../../base-classes/ebo-object-time-period';
import { Route } from '../../route/route';
import { PayUnit, PayUnitSuffixMapping } from '../pay-unit.enum';
import { PaySchedule, PayScheduleMapping } from '../shared/pay-schedule.enum';
import { IncentiveAmountType, IncentiveAmountTypeMapping } from './incentive-amount-type.enum';
import { IncentiveBasis } from './incentive-basis.enum';
import { IncentiveMileageBasis, IncentiveMileageBasisMapping } from './incentive-mileage-basis.enum';
import { IncentivePayWindow } from './incentive-pay-window.enum';
import { IncentiveTrigger, IncentiveTriggerMapping } from './incentive-trigger.enum';
import { IncentiveWageGroup } from './incentive-wage-group';
import {
  IncentiveWageType,
  IncentiveWageTypeMapping,
  IncentiveWagesBasedOnAmount,
  IncentiveWagesBasedOnHours,
  IncentiveWagesBasedOnMileageRate
} from './incentive-wage-type.enum';
import { IncentiveWindowType, IncentiveWindowTypeMapping } from './incentive-window-type.enum';

export class IncentiveWage extends EboObjectTimePeriod implements SBaseDisplay {
  groupId: string;
  group: IncentiveWageGroup;
  ruleOrder: number;
  schedule: PaySchedule;
  window: IncentivePayWindow;
  basis: IncentiveBasis;

  //Filter
  soloLegs: boolean;
  soloMin: number;
  teamLegs: boolean;
  teamMin: number;
  spotLegs: boolean;
  spotMin: number;
  hours: boolean;
  hourMin: number;

  daysOfWeek: DaysOfWeek;
  driverOffDays: boolean;

  routeId: string;
  route: Route;
  holidays: Holidays;
  birthday: boolean;
  serviceAnniversary: boolean;

  //Restrictions
  minimum: number;
  maximum: number;
  includeVacationDays: boolean;
  requireMilesInPeriod: boolean;

  //Window
  windowType: IncentiveWindowType;
  windowStartDayOfWeek: DayOfWeek;
  windowStartTime: Duration;
  windowEndDayOfWeek: DayOfWeek;
  windowEndTime: Duration;
  windowMinimumTime: number;

  //Multiplier
  multiplierInterval: Interval;
  multiplier: number;
  intervalOffset: number;
  intervalMax: number;
  startPriorMonth: MonthEnum;
  startPriorDay: number;
  increaseMonth: MonthEnum;
  increaseDay: number;
  //trigger
  trigger: IncentiveTrigger;

  //Payment Amount
  wageType: IncentiveWageType;
  payIsIncrementalToMin: boolean;
  mileageBasis: IncentiveMileageBasis;
  includeOneWayLH: boolean;
  routeItemize: boolean;
  soloRate: number;
  teamRate: number;
  spotRate: number;
  spotAmountPercentage: number;
  spotPercentagesItemize: boolean;
  units: PayUnit;
  soloGrossPercentage: number;
  teamGrossPercentage: number;
  spotGrossPercentage: number;
  hourlyRate: number;
  hourlyPercentage: number;
  amountType: IncentiveAmountType;
  amount: number;
  quantity: number;
  separateCorrections: boolean;

  constructor(jsonData: Partial<IncentiveWage>) {
    super(jsonData);
    if (jsonData?.windowStartTime) this.windowStartTime = SDuration.new(jsonData.windowStartTime);
    if (jsonData?.windowEndTime) this.windowEndTime = SDuration.new(jsonData.windowEndTime);
    if (jsonData?.route) this.route = new Route(jsonData.route);
    this.basis ??= IncentiveBasis.Always;
    this.wageType ??= IncentiveWageType.FixedAmount;
    this.windowType ??= IncentiveWindowType.NoWindow;
    this.trigger ??= IncentiveTrigger.OneTime;
    this.multiplierInterval ??= Interval.None;
    this.multiplier ??= 1;
    this.intervalOffset ??= 0;
    this.windowMinimumTime ??= 0;
    this.startPriorMonth ??= MonthEnum.December;
    this.startPriorDay ??= 31;
    this.increaseMonth ??= MonthEnum.January;
    this.increaseDay ??= 1;
    this.soloLegs ??= true;
    this.spotLegs ??= true;
    this.teamLegs ??= true;
    this.hours ??= false;
    this.daysOfWeek ??= DaysOfWeek.AllDays;
    this.payIsIncrementalToMin ??= false;
    this.requireMilesInPeriod ??= false;
    this.mileageBasis ??= IncentiveMileageBasis.Total;
    this.amountType ??= IncentiveAmountType.Incremental;
    this.spotPercentagesItemize ??= false;
    this.driverOffDays ??= false;
  }

  get paySummary(): SummitFormattedText {
    const response = new SummitFormattedText();
    if (IncentiveWagesBasedOnAmount.indexOf(this.wageType) >= 0) {
      response.AddKeyValues(
        `${this.quantity ? NumberFormatter(this.quantity) : MoneyFormatter(this.amount)} - ${
          IncentiveWageTypeMapping[this.wageType]
        }`,
        IncentiveAmountTypeMapping[this.amountType]
      );
      switch (this.windowType) {
        case IncentiveWindowType.NoWindow:
          response.AddKeyValues(IncentiveWindowTypeMapping[this.windowType], IncentiveTriggerMapping[this.trigger]);
          if (this.trigger == IncentiveTrigger.DayCount) {
            const logFilter = [];
            if (this.soloLegs) logFilter.push('Solo');
            if (this.teamLegs) logFilter.push('Team');
            if (this.spotLegs) logFilter.push('Spot');
            if (this.hours) logFilter.push('Hours');
            if (logFilter.length > 0) response.AddKeyValues('Legs', logFilter.join(', '));
            if (this.soloLegs && this.soloMin) response.AddKeyValues('Solo Min', this.soloMin);
            if (this.teamLegs && this.teamMin) response.AddKeyValues('Team Min', this.teamMin);
            if (this.spotLegs && this.spotMin) response.AddKeyValues('Spot Min', this.spotMin);
            if (this.hours && this.hourMin) response.AddKeyValues('Hour Min', this.hourMin);
          }
          if (this.minimum) response.AddKeyValues('Min', this.minimum);
          if (this.maximum) response.AddKeyValues('Max', this.maximum);
          if (this.includeVacationDays) response.AddKeyValues('Include Vacation Days', 'Yes');
          break;
        case IncentiveWindowType.DailyWindow:
          response.AddLine(
            `${this.startTime.hour()}:${NumberFormatter(
              this.startTime.minute(),
              2
            )}-${this.endTime.hour()}:${NumberFormatter(this.endTime.minute(), 2)}`
          );
          break;
        case IncentiveWindowType.SingleWindow:
          response.AddLine(
            `${DayOfWeekAbbrMapping[this.windowStartDayOfWeek]} ${this.windowStartTime.hours()}:${NumberFormatter(
              this.startTime.minute(),
              2
            )}- ${DayOfWeekAbbrMapping[this.windowEndDayOfWeek]} ${this.windowEndTime.hours()}:${NumberFormatter(
              this.endTime.minute(),
              2
            )}`
          );
      }
    } else if (
      IncentiveWagesBasedOnMileageRate.indexOf(this.wageType) >= 0 &&
      this.trigger != IncentiveTrigger.PerDay
    ) {
      response.AddLine(`${IncentiveWageTypeMapping[this.wageType]} @`);
      response.AddLine(`${IncentiveMileageBasisMapping[this.mileageBasis]}`);
      if (this.soloRate) response.AddLine(`${RateFormatter(this.soloRate)} Solo`);
      const payUnitSuffix = PayUnitSuffixMapping[this.units];
      if (this.soloGrossPercentage)
        response.AddLine(`${PercentageFormatter(this.soloGrossPercentage / 100)} of Solo ${payUnitSuffix}`);
      if (this.teamRate) response.AddLine(`${RateFormatter(this.teamRate)} Team`);
      if (this.teamGrossPercentage)
        response.AddLine(`${PercentageFormatter(this.teamGrossPercentage / 100)} of Team ${payUnitSuffix}`);
      if (this.spotRate) response.AddLine(`${RateFormatter(this.spotRate)} Spot`);
      if (this.spotGrossPercentage)
        response.AddLine(`${PercentageFormatter(this.spotGrossPercentage / 100)} of Spot ${payUnitSuffix}`);
      if (this.spotAmountPercentage)
        response.AddLine(`${PercentageFormatter(this.spotAmountPercentage / 100)} of Spot Amount`);
      if (this.routeItemize) response.AddLine('Itemize by Route');
      if (this.spotPercentagesItemize) response.AddLine('Itemize Spot Percentages');
    } else if (IncentiveWagesBasedOnHours.indexOf(this.wageType) >= 0) {
      if (this.trigger == IncentiveTrigger.PerDepartureDelay) {
        if (this.minimum) response.AddKeyValues('Departure Delay', `${NumberFormatter(this.minimum ?? 0)} Hr. (Min)`);
        else response.AddLine('Departure Delay');
        if (this.hourlyRate) response.AddKeyValues(`Hourly`, MoneyFormatter(this.hourlyRate));
        if (this.payIsIncrementalToMin) response.AddKeyValues(`Incremental`, 'Only pay incremental to Min.');
      } else if (this.trigger == IncentiveTrigger.PerReRouteDelay) {
        if (this.minimum) response.AddKeyValues('Reroute Delay', `${NumberFormatter(this.minimum ?? 0)} Hr. (Min)`);
        else response.AddLine('Reroute Delay');
        if (this.hourlyRate) response.AddKeyValues(`Hourly`, MoneyFormatter(this.hourlyRate));
        if (this.payIsIncrementalToMin) response.AddKeyValues(`Incremental`, 'Only pay incremental to Min.');
      } else if (this.hours) {
        if (this.hourMin) response.AddKeyValues('Hourly', `${NumberFormatter(this.hourMin)} Hr. (Min)`);
        else response.AddLine('Hourly');
        if (this.amount) response.AddLine(`${MoneyFormatter(this.amount)}`);
        if (this.payIsIncrementalToMin) {
          if (this.amount) response.AddKeyValues('Overage:', `+${MoneyFormatter(this.amount)}`);
          if (this.hourlyPercentage)
            response.AddKeyValues('Overage:', `+${PercentageFormatter(this.hourlyPercentage / 100)}`);
        } else {
          if (this.amount) response.AddLine(`+${MoneyFormatter(this.amount)}`, ['formattedTextRight']);
          if (this.hourlyPercentage)
            response.AddLine(`+${PercentageFormatter(this.hourlyPercentage / 100)}`, ['formattedTextRight']);
        }
      }
    } else {
      //console.log('IncentiveWagesBasedOnHours', this.trigger);
      if (this.trigger == IncentiveTrigger.PerDay) {
        response.AddKeyValues(
          `${this.quantity ? NumberFormatter(this.quantity) : MoneyFormatter(this.amount)} - ${
            IncentiveWageTypeMapping[this.wageType]
          }`,
          IncentiveAmountTypeMapping[this.amountType]
        );
        if (this.payIsIncrementalToMin) response.AddKeyValues(`Incremental`, 'Only pay incremental to Min.');
      }
    }
    if (this.routeId) response.AddKeyValues('Route', this.route?.name);
    if (this.includeOneWayLH) response.AddKeyValues('Include 1 Way LH', 'Yes');
    if (this.daysOfWeek != DaysOfWeek.AllDays) response.AddKeyValues('Days', DaysOfWeekToString(this.daysOfWeek));
    if (this.driverOffDays) response.AddKeyValues('Days', 'Driver Off Days');
    if (this.holidays) response.AddKeyValues('Holidays', HolidaysToString(this.holidays));
    if (this.birthday) response.AddKeyValues('Birthday', 'Yes');
    if (this.serviceAnniversary) response.AddKeyValues('Service Anniversary', 'Yes');
    if (this.separateCorrections) response.AddKeyValues('Separate Corrections', 'Yes');

    return response.lines.length > 0 ? response : null;
  }

  HasDay(flag: DaysOfWeek): boolean {
    // tslint:disable-next-line: no-bitwise
    return flag === (this.daysOfWeek & flag);
  }

  SetDay(flag: DaysOfWeek, value: boolean) {
    if (value) {
      // tslint:disable-next-line: no-bitwise
      this.daysOfWeek |= flag;
    } else {
      // tslint:disable-next-line: no-bitwise
      this.daysOfWeek &= ~flag;
    }
  }

  toShortString(): string {
    const response = `${PayScheduleMapping[this.schedule]} - ${this.paySummary}`;
    return response;
  }

  get defaultSort() {
    return `${this.endTime ? this.endTime.format('YYYYMMDD') : '00000000'}-${
      this.group ? this.group.name : '~'
    }-${this.startTime?.format('YYYYMMDD')}-${NumberSortString(this.ruleOrder, 2)}`;
  }
}
